import { DateTime } from "luxon";
import { v4 as uuidv4 } from "uuid";
import type { Department } from "../../../contexts/AuthProvider";
import { getDepartmentWageConfig } from "../../../utils/DepartmentWage.config";
import { parseTimeString } from "../../../utils/dateUtils";
import {
	type AutoApproveRule,
	type BreakRulesConfig,
	DEFAULT_WEEK_START_DAY,
	type DepartmentLocation,
	DepartmentType,
	EmploymentType,
	type EmrIntegrationInput,
	type HoursRateConfig,
	NurseRole,
	type OvertimeConfig,
	OvertimeConfigType,
	RateHoursBonusType,
	RemoveUnionStaffWarning,
	ShiftType,
	StatsMode,
	uniformHppdTarget,
} from "../../../utils/models";
import { breakRulesConfigFromApi, hoursRateConfigFromApi, overtimeConfigFromApi } from "../../../utils/wageRules/fromApi";
import { DIFF_HOURS_WARNING } from "../../../utils/warningDialogs/utils";
import type { AdminRole, AdminUser, GeneralFormData } from "./forms/types";
import { TrainingWageType } from "./forms/types";

export const TIMEZONES = [
	"Africa/Abidjan",
	"Africa/Accra",
	"Africa/Algiers",
	"Africa/Bissau",
	"Africa/Cairo",
	"Africa/Casablanca",
	"Africa/Ceuta",
	"Africa/El_Aaiun",
	"Africa/Johannesburg",
	"Africa/Juba",
	"Africa/Khartoum",
	"Africa/Lagos",
	"Africa/Maputo",
	"Africa/Monrovia",
	"Africa/Nairobi",
	"Africa/Ndjamena",
	"Africa/Sao_Tome",
	"Africa/Tripoli",
	"Africa/Tunis",
	"Africa/Windhoek",
	"America/Adak",
	"America/Anchorage",
	"America/Araguaina",
	"America/Argentina/Buenos_Aires",
	"America/Argentina/Catamarca",
	"America/Argentina/Cordoba",
	"America/Argentina/Jujuy",
	"America/Argentina/La_Rioja",
	"America/Argentina/Mendoza",
	"America/Argentina/Rio_Gallegos",
	"America/Argentina/Salta",
	"America/Argentina/San_Juan",
	"America/Argentina/San_Luis",
	"America/Argentina/Tucuman",
	"America/Argentina/Ushuaia",
	"America/Asuncion",
	"America/Atikokan",
	"America/Bahia",
	"America/Bahia_Banderas",
	"America/Barbados",
	"America/Belem",
	"America/Belize",
	"America/Blanc-Sablon",
	"America/Boa_Vista",
	"America/Bogota",
	"America/Boise",
	"America/Cambridge_Bay",
	"America/Campo_Grande",
	"America/Cancun",
	"America/Caracas",
	"America/Cayenne",
	"America/Chicago",
	"America/Chihuahua",
	"America/Costa_Rica",
	"America/Creston",
	"America/Cuiaba",
	"America/Curacao",
	"America/Danmarkshavn",
	"America/Dawson",
	"America/Dawson_Creek",
	"America/Denver",
	"America/Detroit",
	"America/Edmonton",
	"America/Eirunepe",
	"America/El_Salvador",
	"America/Fort_Nelson",
	"America/Fortaleza",
	"America/Glace_Bay",
	"America/Godthab",
	"America/Goose_Bay",
	"America/Grand_Turk",
	"America/Guatemala",
	"America/Guayaquil",
	"America/Guyana",
	"America/Halifax",
	"America/Havana",
	"America/Hermosillo",
	"America/Indiana/Indianapolis",
	"America/Indiana/Knox",
	"America/Indiana/Marengo",
	"America/Indiana/Petersburg",
	"America/Indiana/Tell_City",
	"America/Indiana/Vevay",
	"America/Indiana/Vincennes",
	"America/Indiana/Winamac",
	"America/Inuvik",
	"America/Iqaluit",
	"America/Jamaica",
	"America/Juneau",
	"America/Kentucky/Louisville",
	"America/Kentucky/Monticello",
	"America/La_Paz",
	"America/Lima",
	"America/Los_Angeles",
	"America/Maceio",
	"America/Managua",
	"America/Manaus",
	"America/Martinique",
	"America/Matamoros",
	"America/Mazatlan",
	"America/Menominee",
	"America/Merida",
	"America/Metlakatla",
	"America/Mexico_City",
	"America/Miquelon",
	"America/Moncton",
	"America/Monterrey",
	"America/Montevideo",
	"America/Nassau",
	"America/New_York",
	"America/Nipigon",
	"America/Nome",
	"America/Noronha",
	"America/North_Dakota/Beulah",
	"America/North_Dakota/Center",
	"America/North_Dakota/New_Salem",
	"America/Ojinaga",
	"America/Panama",
	"America/Pangnirtung",
	"America/Paramaribo",
	"America/Phoenix",
	"America/Port-au-Prince",
	"America/Port_of_Spain",
	"America/Porto_Velho",
	"America/Puerto_Rico",
	"America/Punta_Arenas",
	"America/Rainy_River",
	"America/Rankin_Inlet",
	"America/Recife",
	"America/Regina",
	"America/Resolute",
	"America/Rio_Branco",
	"America/Santarem",
	"America/Santiago",
	"America/Santo_Domingo",
	"America/Sao_Paulo",
	"America/Scoresbysund",
	"America/Sitka",
	"America/St_Johns",
	"America/Swift_Current",
	"America/Tegucigalpa",
	"America/Thule",
	"America/Thunder_Bay",
	"America/Tijuana",
	"America/Toronto",
	"America/Vancouver",
	"America/Whitehorse",
	"America/Winnipeg",
	"America/Yakutat",
	"America/Yellowknife",
	"Antarctica/Casey",
	"Antarctica/Davis",
	"Antarctica/DumontDUrville",
	"Antarctica/Macquarie",
	"Antarctica/Mawson",
	"Antarctica/Palmer",
	"Antarctica/Rothera",
	"Antarctica/Syowa",
	"Antarctica/Troll",
	"Antarctica/Vostok",
	"Asia/Almaty",
	"Asia/Amman",
	"Asia/Anadyr",
	"Asia/Aqtau",
	"Asia/Aqtobe",
	"Asia/Ashgabat",
	"Asia/Atyrau",
	"Asia/Baghdad",
	"Asia/Baku",
	"Asia/Bangkok",
	"Asia/Barnaul",
	"Asia/Beirut",
	"Asia/Bishkek",
	"Asia/Brunei",
	"Asia/Chita",
	"Asia/Choibalsan",
	"Asia/Colombo",
	"Asia/Damascus",
	"Asia/Dhaka",
	"Asia/Dili",
	"Asia/Dubai",
	"Asia/Dushanbe",
	"Asia/Famagusta",
	"Asia/Gaza",
	"Asia/Hebron",
	"Asia/Ho_Chi_Minh",
	"Asia/Hong_Kong",
	"Asia/Hovd",
	"Asia/Irkutsk",
	"Asia/Jakarta",
	"Asia/Jayapura",
	"Asia/Jerusalem",
	"Asia/Kabul",
	"Asia/Kamchatka",
	"Asia/Karachi",
	"Asia/Kathmandu",
	"Asia/Khandyga",
	"Asia/Kolkata",
	"Asia/Krasnoyarsk",
	"Asia/Kuala_Lumpur",
	"Asia/Kuching",
	"Asia/Macau",
	"Asia/Magadan",
	"Asia/Makassar",
	"Asia/Manila",
	"Asia/Nicosia",
	"Asia/Novokuznetsk",
	"Asia/Novosibirsk",
	"Asia/Omsk",
	"Asia/Oral",
	"Asia/Pontianak",
	"Asia/Pyongyang",
	"Asia/Qatar",
	"Asia/Qostanay",
	"Asia/Qyzylorda",
	"Asia/Riyadh",
	"Asia/Sakhalin",
	"Asia/Samarkand",
	"Asia/Seoul",
	"Asia/Shanghai",
	"Asia/Singapore",
	"Asia/Srednekolymsk",
	"Asia/Taipei",
	"Asia/Tashkent",
	"Asia/Tbilisi",
	"Asia/Tehran",
	"Asia/Thimphu",
	"Asia/Tokyo",
	"Asia/Tomsk",
	"Asia/Ulaanbaatar",
	"Asia/Urumqi",
	"Asia/Ust-Nera",
	"Asia/Vladivostok",
	"Asia/Yakutsk",
	"Asia/Yangon",
	"Asia/Yekaterinburg",
	"Asia/Yerevan",
	"Atlantic/Azores",
	"Atlantic/Bermuda",
	"Atlantic/Canary",
	"Atlantic/Cape_Verde",
	"Atlantic/Faroe",
	"Atlantic/Madeira",
	"Atlantic/Reykjavik",
	"Atlantic/South_Georgia",
	"Atlantic/Stanley",
	"Australia/Adelaide",
	"Australia/Brisbane",
	"Australia/Broken_Hill",
	"Australia/Currie",
	"Australia/Darwin",
	"Australia/Eucla",
	"Australia/Hobart",
	"Australia/Lindeman",
	"Australia/Lord_Howe",
	"Australia/Melbourne",
	"Australia/Perth",
	"Australia/Sydney",
	"Europe/Amsterdam",
	"Europe/Andorra",
	"Europe/Astrakhan",
	"Europe/Athens",
	"Europe/Belgrade",
	"Europe/Berlin",
	"Europe/Brussels",
	"Europe/Bucharest",
	"Europe/Budapest",
	"Europe/Chisinau",
	"Europe/Copenhagen",
	"Europe/Dublin",
	"Europe/Gibraltar",
	"Europe/Helsinki",
	"Europe/Istanbul",
	"Europe/Kaliningrad",
	"Europe/Kiev",
	"Europe/Kirov",
	"Europe/Lisbon",
	"Europe/London",
	"Europe/Luxembourg",
	"Europe/Madrid",
	"Europe/Malta",
	"Europe/Minsk",
	"Europe/Monaco",
	"Europe/Moscow",
	"Europe/Oslo",
	"Europe/Paris",
	"Europe/Prague",
	"Europe/Riga",
	"Europe/Rome",
	"Europe/Samara",
	"Europe/Saratov",
	"Europe/Simferopol",
	"Europe/Sofia",
	"Europe/Stockholm",
	"Europe/Tallinn",
	"Europe/Tirane",
	"Europe/Ulyanovsk",
	"Europe/Uzhgorod",
	"Europe/Vienna",
	"Europe/Vilnius",
	"Europe/Volgograd",
	"Europe/Warsaw",
	"Europe/Zaporozhye",
	"Europe/Zurich",
	"Indian/Chagos",
	"Indian/Christmas",
	"Indian/Cocos",
	"Indian/Kerguelen",
	"Indian/Mahe",
	"Indian/Maldives",
	"Indian/Mauritius",
	"Indian/Reunion",
	"Pacific/Apia",
	"Pacific/Auckland",
	"Pacific/Bougainville",
	"Pacific/Chatham",
	"Pacific/Chuuk",
	"Pacific/Easter",
	"Pacific/Efate",
	"Pacific/Enderbury",
	"Pacific/Fakaofo",
	"Pacific/Fiji",
	"Pacific/Funafuti",
	"Pacific/Galapagos",
	"Pacific/Gambier",
	"Pacific/Guadalcanal",
	"Pacific/Guam",
	"Pacific/Honolulu",
	"Pacific/Kiritimati",
	"Pacific/Kosrae",
	"Pacific/Kwajalein",
	"Pacific/Majuro",
	"Pacific/Marquesas",
	"Pacific/Nauru",
	"Pacific/Niue",
	"Pacific/Norfolk",
	"Pacific/Noumea",
	"Pacific/Pago_Pago",
	"Pacific/Palau",
	"Pacific/Pitcairn",
	"Pacific/Pohnpei",
	"Pacific/Port_Moresby",
	"Pacific/Rarotonga",
	"Pacific/Tahiti",
	"Pacific/Tarawa",
	"Pacific/Tongatapu",
	"Pacific/Wake",
	"Pacific/Wallis",
] as const satisfies readonly string[];

export const SHIFT_ALERT_TIMES_MINUTES = [15, 30, 60, 120, 240, 480, 600, 720, 1440] as const satisfies readonly number[];
export const PARTIAL_SHIFT_BLOCK_HOURS = [6] as const satisfies readonly number[];
// DAT-814: mirrors the backend's DEFAULT_*_WAGE_TYPE / _VALUE in models/wage_config/config.py. The
// new-department form seeds itself from these and POSTs them verbatim, so this side — not the
// backend default — is what a department created through the UI actually gets. The two must move
// together. Percentage/100 means "the employee's own wage"; existing departments keep what they have.
export const DEFAULT_ORIENTATION_WAGE_TYPE = TrainingWageType.Percentage;
export const DEFAULT_ORIENTATION_WAGE_AMOUNT = 100;
export const DEFAULT_TRAINING_WAGE_TYPE = TrainingWageType.Percentage;
export const DEFAULT_TRAINING_WAGE_AMOUNT = 100;
export const DEFAULT_OVERTIME_RATE_PAY_PERCENTAGE = 150;
export const DEFAULT_HOLIDAY_RATE_PAY_PERCENTAGE = 200;
export const DEFAULT_NIGHT_PAY_VALUE: Partial<Record<NurseRole, number>> = Object.fromEntries(Object.values(NurseRole).map((role) => [role, 0]));
export const DEFAULT_WEEKEND_PAY_VALUE: Partial<Record<NurseRole, number>> = Object.fromEntries(Object.values(NurseRole).map((role) => [role, 0]));
export const DEFAULT_OVERTIME_DAILY_HOURS_LIMIT = 12;
export const DEFAULT_OVERTIME_WEEKLY_HOURS_LIMIT = 40;
const DEFAULT_NEW_DEPT_DAY_START_HOUR = 6;

export const createDefaultBreakRulesConfig = (): BreakRulesConfig[] => [
	{
		id: uuidv4(),
		name: "Standard Break",
		breakLimit: 5,
		roles: Object.values(NurseRole),
		employmentTypes: Object.values(EmploymentType),
		type: "addition",
		breakTime: 30,
		breakStart: 5,
		union: [],
		isUnion: null,
		isPaid: false,
		departmentId: "",
	} as BreakRulesConfig,
];

export const createDefaultOvertimeConfig = (): OvertimeConfig[] => {
	const defaultEmploymentTypes = (Object.values(EmploymentType) as EmploymentType[]).filter((type) => type !== EmploymentType.PerDiem);
	return [
		{
			id: uuidv4(),
			name: "Overtime Daily",
			hoursLimit: DEFAULT_OVERTIME_DAILY_HOURS_LIMIT,
			roles: Object.values(NurseRole),
			employmentType: defaultEmploymentTypes,
			union: [],
			type: OvertimeConfigType.daily,
			rate: DEFAULT_OVERTIME_RATE_PAY_PERCENTAGE,
			rateType: RateHoursBonusType.Percentage,
			departmentId: "",
		} as OvertimeConfig,
		{
			id: uuidv4(),
			name: "Overtime Weekly",
			hoursLimit: DEFAULT_OVERTIME_WEEKLY_HOURS_LIMIT,
			roles: Object.values(NurseRole),
			employmentType: defaultEmploymentTypes,
			union: [],
			type: OvertimeConfigType.weekly,
			rate: DEFAULT_OVERTIME_RATE_PAY_PERCENTAGE,
			rateType: RateHoursBonusType.Percentage,
			departmentId: "",
		},
	] as OvertimeConfig[];
};

/**
 * The staff-schedule horizon is a calendar day, but the two sides of a comparison can be a `DateTime`
 * (context/form) or absent (no horizon). Compare on the ISO date so a zone or time-of-day difference
 * doesn't read as a change.
 */
export const isSameScheduleVisibilityDate = (a: DateTime | null | undefined, b: DateTime | null | undefined): boolean =>
	(a?.toISODate() ?? null) === (b?.toISODate() ?? null);

/**
 * The department config create/update responses aren't run through `convertIsoDates`, so a date can
 * come back as a raw ISO string. Normalize so the form always holds a `DateTime` for the picker.
 */
const toDateTime = (value: DateTime | string | null | undefined): DateTime | null => {
	if (!value) return null;
	return typeof value === "string" ? DateTime.fromISO(value) : value;
};

const HOURS_PER_DAY = 24;

/**
 * Resolve the API's OT-release config into the form's two mutually-exclusive fields, so the control
 * can read the unit off "which one is non-null".
 *
 * `overtimeEligibilityReleaseHours` is the backend's source of truth; `overtimeEligibilityReleaseDays`
 * is the legacy `hours // 24` mirror, so we prefer hours and fall back to `days * 24` only for a
 * response predating the hours column. A whole-day multiple reads as Days (the historical unit,
 * least surprising for how these were configured); a sub-day remainder reads as Hours.
 */
function resolveOvertimeReleaseFields(
	days: number | null | undefined,
	hours: number | null | undefined,
): Pick<GeneralFormData, "overtimeEligibilityReleaseDays" | "overtimeEligibilityReleaseHours"> {
	const effectiveHours = hours ?? (days != null ? days * HOURS_PER_DAY : null);
	if (effectiveHours == null) {
		return { overtimeEligibilityReleaseDays: null, overtimeEligibilityReleaseHours: null };
	}
	if (effectiveHours % HOURS_PER_DAY === 0) {
		return { overtimeEligibilityReleaseDays: effectiveHours / HOURS_PER_DAY, overtimeEligibilityReleaseHours: null };
	}
	return { overtimeEligibilityReleaseDays: null, overtimeEligibilityReleaseHours: effectiveHours };
}

export function getFormDataFromDept(dept?: Department): GeneralFormData {
	if (!dept) {
		return {
			displayName: "",
			hospital: "",
			targetHPPD: 0,
			targetHPPDPerDay: uniformHppdTarget(0),
			targetPPD: 1,
			minValidHPPDPercent: 94,
			maxValidHPPDPercent: 106,
			week1FirstSunday: (() => {
				const jan1 = DateTime.now().startOf("year");
				const daysUntilSunday = jan1.weekday === 7 ? 0 : 7 - jan1.weekday;
				return jan1.plus({ days: daysUntilSunday });
			})(),
			totalBeds: 50,
			// Start empty so the user must explicitly choose a timezone when creating a department.
			timezone: "",
			roles: [],
			notifyBeforeShiftStartMintutes: undefined,
			notifyBeforeShiftEndMintutes: undefined,
			partialBlockShifts: { default: [] },
			fullShiftTimes: { default: [] },
			jobShiftTimes: {},
			jobPpdOverrides: {},
			masterScheduleWeeks: 2,
			canViewNurses: false,
			// A new department publishes its whole schedule until Ops sets a horizon.
			staffScheduleDisplayUntilDate: null,
			hideOverstaffingWarning: false,
			countBedholdInCensus: false,
			censusIntervals: null,
			combineConsecutiveAgencyShifts: true,
			agencyPostDelayDefaultHours: 72,
			prnCycleDurationDays: 0,
			nurseInactivityAlertDays: 70,
			nurseInactiveAfterDays: 90,
			departmentDataHistoryWeeks: 5,
			autoCreateScheduleWeeksAhead: 8,
			roleGroups: {},
			emrIntegrations: [],
			hrisIntegrations: [],
			statsMode: StatsMode.PPD_HPPD,
			removeUnionStaffWarning: RemoveUnionStaffWarning.None,
			defaultRole: undefined,
			departmentWageConfig: {
				...getDepartmentWageConfig(undefined),
			},
			specialNurses: {},
			holidayBoundaries: { startHour: 6.0, endHour: 6.0 },
			dayShiftBoundaries: { startHour: 6.0, endHour: 18.0 },
			weekendBoundaries: { startDay: 5, startHour: 18.0, endDay: 1, endHour: 6.0 },
			weekStartDay: DEFAULT_WEEK_START_DAY,
			hoursRateConfig: [
				{
					startTime: null,
					endTime: null,
					bonusVal: 0.0,
					bonusType: RateHoursBonusType.FlatRate,
					roles: [],
					departmentId: "",
					name: "",
					trainingStatuses: [],
					employmentTypes: [],
				} as HoursRateConfig,
			],
			hrisPositions: [],
			overtimeConfig: createDefaultOvertimeConfig(),
			breakRulesConfig: createDefaultBreakRulesConfig(),
			dayStart: DEFAULT_NEW_DEPT_DAY_START_HOUR,
			// DAT-527: null so a new department starts on the legacy OT clock (see forms/types.ts).
			payrollStartTime: null,
			// null so a new department inherits the backend's call-off window defaults.
			callOffBypassHoursBeforeShiftBedside: null,
			callOffBypassHoursAfterStartBedside: null,
			callOffBypassHoursBeforeShiftAdmin: null,
			callOffBypassHoursAfterStartAdmin: null,
			// DEFAULT: a new department preserves the pickup agent's legacy OT notification targeting until Ops sets a release window.
			overtimeEligibilityReleaseDays: null,
			overtimeEligibilityReleaseHours: null,
			clipboardFacilityId: undefined,
			nursaFacilityId: undefined,
			lateArrivalMinutes: 60,
			earlyDepartureMinutes: 60,
			shiftDeltaMinutes: 120,
			scheduledShiftDeltaMinutes: 120,
			shiftDeltaThresholdHours: 2,
			// User must pick a department type when creating a new department; no implicit SNF default.
			departmentType: undefined,
			jobIds: [],
			longShiftDuration: DIFF_HOURS_WARNING,
		};
	}

	const {
		department_id,
		display_name,
		hospital,
		hppd,
		hppd_target,
		ppd,
		min_valid_hppd_percent,
		max_valid_hppd_percent,
		first_sunday,
		timezone,
		notify_before_shift_start_minutes,
		notify_before_shift_end_minutes,
		roles = [],
		partial_block_shifts,
		full_shift_times,
		total_beds,
		master_schedule_weeks,
		can_view_nurses,
		prn_cycle_duration_days,
		role_groups,
		emrIntegrations: deptEmrIntegrations,
		default_role,
		holiday_boundaries,
		day_shift_boundaries,
		weekend_boundaries,
		week_start_day,
		department_wage_config,
		special_nurses,
		statsMode,
		removeUnionStaffWarning,
		hoursRateConfig,
		callOffBypassHoursBeforeShiftBedside,
		callOffBypassHoursAfterStartBedside,
		callOffBypassHoursBeforeShiftAdmin,
		callOffBypassHoursAfterStartAdmin,
		callOffMessageLeadTimeMinutes,
		overtimeEligibilityReleaseDays,
		overtimeEligibilityReleaseHours,
		dayStart,
		payrollStartTime,
		overtimeConfig,
		breakRulesConfig,
		autoApproveRules,
		late_arrival_minutes,
		early_departure_minutes,
		shift_delta_minutes,
		scheduled_shift_delta_minutes,
		shift_delta_threshold_hours,
		hrisPositions,
		jobIds,
		longShiftDuration,
		hrisIntegrations,
		hideOverstaffingWarning,
		staffScheduleDisplayUntilDate,
		countBedholdInCensus,
		censusIntervals,
		combineConsecutiveAgencyShifts,
		agencyPostDelayDefaultHours,
		departmentDataHistoryWeeks,
		autoCreateScheduleWeeksAhead,
		clipboard_facility_id,
		nursa_facility_id,
	} = dept;

	const partialBlockShifts = roles.reduce<Department["partial_block_shifts"]>((accu, current) => {
		accu[current] = partial_block_shifts[current] || [];
		return accu;
	}, {});
	partialBlockShifts.default = partial_block_shifts.default || [];

	const fullShiftTimes = roles.reduce<Department["full_shift_times"]>((accu, current) => {
		accu[current] = full_shift_times[current] || [];
		return accu;
	}, {});
	fullShiftTimes.default = full_shift_times.default || [];

	const jobShiftTimes = (dept.jobShiftTimes ?? []).reduce<GeneralFormData["jobShiftTimes"]>((accu, row) => {
		accu[row.jobId] = { partialBlockShifts: row.partialBlockShifts, fullShiftTimes: row.fullShiftTimes };
		return accu;
	}, {});

	const emrIntegrations: EmrIntegrationInput[] = (deptEmrIntegrations ?? []).map(({ type, facilityId, orgId, active }) => ({
		type,
		facilityId,
		orgId,
		active,
	}));

	return {
		id: department_id,
		displayName: display_name,
		hospital,
		targetHPPD: hppd,
		targetHPPDPerDay: hppd_target,
		targetPPD: ppd,
		minValidHPPDPercent: min_valid_hppd_percent,
		maxValidHPPDPercent: max_valid_hppd_percent,
		week1FirstSunday: first_sunday || DateTime.fromFormat("2020-05-03", "yyyy-MM-dd"),
		totalBeds: total_beds,
		timezone,
		roles,
		notifyBeforeShiftStartMintutes: notify_before_shift_start_minutes,
		notifyBeforeShiftEndMintutes: notify_before_shift_end_minutes,
		partialBlockShifts,
		fullShiftTimes,
		jobShiftTimes,
		// Pending PPD edits are session state, never loaded from the department — a fresh form has none.
		jobPpdOverrides: {},
		masterScheduleWeeks: master_schedule_weeks,
		canViewNurses: can_view_nurses,
		// Absent means the department has no visibility horizon at all — null must round-trip as null.
		staffScheduleDisplayUntilDate: toDateTime(staffScheduleDisplayUntilDate),
		hideOverstaffingWarning: hideOverstaffingWarning ?? false,
		// DEFAULT: new departments / older API responses have no value yet; false matches the DB column default.
		countBedholdInCensus: countBedholdInCensus ?? false,
		// `?? null`, never `?? []`: [] is rejected by the column CHECK, and the backend reads it as
		// "average over no readings" — census 0 every day. Absent means the feature is off.
		censusIntervals: censusIntervals ?? null,
		// DEFAULT: true matches the DB column default, so an older API response reads as "merging on"
		// rather than silently showing the department as opted out of merging.
		combineConsecutiveAgencyShifts: combineConsecutiveAgencyShifts ?? true,
		// DEFAULT: 72 matches the DB column default, for API responses predating the column.
		agencyPostDelayDefaultHours: agencyPostDelayDefaultHours ?? 72,
		prnCycleDurationDays: prn_cycle_duration_days,
		roleGroups: role_groups,
		emrIntegrations,
		hrisIntegrations: hrisIntegrations ?? [],
		statsMode: statsMode ?? StatsMode.PPD_HPPD,
		removeUnionStaffWarning: removeUnionStaffWarning ?? RemoveUnionStaffWarning.None,
		defaultRole: default_role,
		holidayBoundaries: holiday_boundaries ? { startHour: holiday_boundaries.startHour, endHour: holiday_boundaries.endHour } : undefined,
		dayShiftBoundaries: day_shift_boundaries ? { startHour: day_shift_boundaries.startHour, endHour: day_shift_boundaries.endHour } : undefined,
		weekendBoundaries: weekend_boundaries
			? {
					startDay: weekend_boundaries.startDay,
					startHour: weekend_boundaries.startHour,
					endDay: weekend_boundaries.endDay,
					endHour: weekend_boundaries.endHour,
				}
			: undefined,
		weekStartDay: week_start_day,
		departmentWageConfig: department_wage_config,
		specialNurses: special_nurses,
		// Normalized to null: the form models "unset" as null only, so an absent key from the API
		// does not become a third state the inputs would have to handle.
		callOffBypassHoursBeforeShiftBedside: callOffBypassHoursBeforeShiftBedside ?? null,
		callOffBypassHoursAfterStartBedside: callOffBypassHoursAfterStartBedside ?? null,
		callOffBypassHoursBeforeShiftAdmin: callOffBypassHoursBeforeShiftAdmin ?? null,
		callOffBypassHoursAfterStartAdmin: callOffBypassHoursAfterStartAdmin ?? null,
		callOffMessageLeadTimeMinutes: callOffMessageLeadTimeMinutes,
		// Split the backend's single hours source-of-truth into the form's Days/Hours pair. An absent
		// field from an older API response resolves to null (feature off), preserving legacy targeting.
		...resolveOvertimeReleaseFields(overtimeEligibilityReleaseDays, overtimeEligibilityReleaseHours),
		clipboardFacilityId: clipboard_facility_id ?? null,
		nursaFacilityId: nursa_facility_id ?? null,
		hoursRateConfig: hoursRateConfigFromApi(hoursRateConfig as HoursRateConfig[] | null | undefined, roles as NurseRole[]),
		dayStart: dayStart ? parseTimeString(dayStart).hour + parseTimeString(dayStart).minute / 60 : 4,
		// DAT-527: deliberately NOT defaulted to dayStart — null must round-trip as null so the
		// column stays NULL for departments that were never decoupled.
		payrollStartTime: payrollStartTime ? parseTimeString(payrollStartTime).hour + parseTimeString(payrollStartTime).minute / 60 : null,
		overtimeConfig: overtimeConfigFromApi(overtimeConfig as OvertimeConfig[] | null | undefined, roles as NurseRole[]),
		breakRulesConfig: breakRulesConfigFromApi(breakRulesConfig as BreakRulesConfig[] | null | undefined, roles as NurseRole[]),
		// Pass rules through as the backend stores them. Canonical "all" markers (jobIds/shiftDays: [],
		// shiftHours: []) are preserved so the round-trip is lossless; the table reads them as "all".
		autoApproveRules: (autoApproveRules ?? []).map((rule: AutoApproveRule) => ({
			...rule,
			jobIds: rule.jobIds ?? [],
			shiftDays: rule.shiftDays ?? [],
			shiftHours: rule.shiftHours ?? [],
			maxOt: rule.maxOt ?? null,
			timeBeforeShift: rule.timeBeforeShift ?? null,
			staffingShortage: rule.staffingShortage ?? null,
			// DEFAULT: a server rule always carries departmentId; fall back to this dept only to
			// defend against a malformed row so it stays filed under the dept being edited.
			departmentId: rule.departmentId ?? department_id,
		})),
		hrisPositions: hrisPositions ?? [],
		lateArrivalMinutes: late_arrival_minutes,
		earlyDepartureMinutes: early_departure_minutes,
		shiftDeltaMinutes: shift_delta_minutes,
		scheduledShiftDeltaMinutes: scheduled_shift_delta_minutes,
		shiftDeltaThresholdHours: shift_delta_threshold_hours,
		departmentType: dept.type ?? DepartmentType.SNF,
		jobIds: [...new Set(jobIds ?? [])],
		longShiftDuration: longShiftDuration ?? DIFF_HOURS_WARNING,
		nurseInactivityAlertDays: dept.nurse_inactivity_alert_days ?? null,
		nurseInactiveAfterDays: dept.nurse_inactive_after_days ?? null,
		departmentDataHistoryWeeks,
		autoCreateScheduleWeeksAhead,
	};
}

export const DEFAULT_ADMIN_FORM_ROW = {
	// Typed here because the object's `as const` would otherwise make this `readonly []`.
	adminRoles: [] as AdminRole[],
	firstName: "firstName",
	phoneNumber: "",
	username: "username@inhouse.health",
	requiresMFA: false,
	isReadonly: false,
	hideWage: true,
	disableEditMasterSchedule: false,
	callOffAdmin: false,
	support: false,
	modifyUser: false,
	receiveDailyAdminNotifications: false,
	receiveActionRequiredAdminNotifications: false,
	sendDailyActualShiftsCheck: false,
	sendDailyPickupSummary: false,
	receivePerformanceReport: false,
	receiveDailyExcelReport: false,
	receiveSmsResponseNotifications: false,
	receiveNurseInactivityNotifications: false,
} as const satisfies AdminUser;

export const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const satisfies string[];

export const DEFAULT_DEPARTMENT_LOCATION: Readonly<DepartmentLocation> = {
	id: uuidv4(),
	section: "",
	location: "",
	location_short_name: "",
	roles: [],
	days: [...WEEKDAYS],
	week_numbers: [1, 2],
	shift_start: "07:00",
	shift_end: "19:00",
	shift_type: ShiftType.Day,
	is_charge: false,
	department_id: "",
	dayAmounts: Object.fromEntries(WEEKDAYS.map((d) => [d, { amount: 1, amount_required: 0 }])),
	amount: 1,
	amountRequired: 0,
	rooms: [],
	cantMove: false,
};

export const TIME_PAIR = /(\d{1,2}):(\d{1,2})\s*-\s*(\d{1,2}):(\d{1,2})/;

/**
 * Shift hours are floats on a 24h clock. Mirrors MAX_SHIFT_HOUR_EXCLUSIVE in
 * services/department_job_shift_times.py: a value the backend would reject with a 400 must never
 * render as a chip here.
 */
const MAX_SHIFT_HOUR_EXCLUSIVE = 24;

/**
 * What a Shift Lengths cell could read, and what it could not.
 *
 * `rejected` exists so the editor can say what it ignored. Dropping unreadable entries silently is
 * what made shift times look unsaveable in DAT-728: the value never became a chip, the form never
 * went dirty, and nothing explained why.
 */
export type ParsedShiftLengthEntries<T> = { values: T[]; rejected: string[] };

/** Parse partial shift lengths (plain hour counts) out of comma- or space-separated entries. */
export function parsePartialShiftHourEntries(entries: string[]): ParsedShiftLengthEntries<number> {
	const hours = new Set<number>();
	const rejected: string[] = [];

	for (const entry of entries) {
		const tokens = entry.split(/[\s,]+/).filter(Boolean);
		for (const token of tokens) {
			const value = Number(token);
			// Same bounds as the backend's validate_job_shift_times, so a chip can never turn into a 400.
			if (!Number.isFinite(value) || value <= 0 || value > MAX_SHIFT_HOUR_EXCLUSIVE) {
				rejected.push(token);
				continue;
			}
			hours.add(value);
		}
	}

	return { values: [...hours], rejected };
}

/**
 * Normalize a time coming from EITHER the server or the form into float hours.
 *
 * The form stores time fields as float hours (23.5 = 23:30), but the backend echoes them back as
 * `"HH:MM:SS"`. Anything that re-seeds form state from a server response has to convert, or
 * `floatToTime` receives a string, `Math.trunc` yields NaN, and the field renders "NaN:NaN" until
 * the page is reloaded. Returns null for absent/unparseable input rather than NaN.
 */
export function toFloatHours(value: string | number | null | undefined): number | null {
	if (value === null || value === undefined || value === "") return null;
	if (typeof value === "number") return Number.isFinite(value) ? value : null;
	const { hour, minute } = parseTimeString(value);
	if (!Number.isFinite(hour)) return null;
	return hour + (Number.isFinite(minute) ? minute : 0) / 60;
}

export const DEFAULT_TARGET_PPD = 1;

export function transformJobShiftTimesForBackend(
	jobShiftTimes: GeneralFormData["jobShiftTimes"],
	enabledJobIds: string[],
	persistedJobIds: Set<string>,
): { jobId: string; partialBlockShifts: number[]; fullShiftTimes: [start: number, end: number][] }[] {
	const enabled = new Set(enabledJobIds);
	return Object.entries(jobShiftTimes)
		.filter(([jobId]) => enabled.has(jobId))
		.filter(([jobId, value]) => value.partialBlockShifts.length > 0 || value.fullShiftTimes.length > 0 || persistedJobIds.has(jobId))
		.map(([jobId, value]) => ({ jobId, partialBlockShifts: value.partialBlockShifts, fullShiftTimes: value.fullShiftTimes }));
}
